import { NetworkStatus, useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { FundClosingStatusEnumType } from '__generated__/graphql';
import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';

import LoadingIndicator from 'primitives/LoadingIndicator';
import TextField from 'primitives/TextField';

import debounce from 'utils/debounce';

const FUND_CLOSINGS_QUERY = gql(`
  query getCompletedFundCommitmentsOfUser(
    $cursor: ID
    $limit: Int
    $filters: DashboardFundClosingsFilterType
  ) {
    dashboard {
      id
      fundClosings(cursor: $cursor, limit: $limit, filters: $filters) {
        nodes {
          id
          fund {
            id
            image
            name
            statistics {
              multipleOnInvestment
            }
          }
          transferredAmount
          investmentEntity {
            id
            name
          }
          amount
          status
          wiredAt
          isV2Closing
        }
        pageInfo {
          hasNextPage
          cursor
        }
      }
    }
  }
`);

const FundCommitmentsOfUser: FC = () => {
  const navigate = useNavigate();
  const { loading, error, data, refetch, fetchMore, variables, networkStatus } = useQuery(
    FUND_CLOSINGS_QUERY,
    {
      variables: {
        filters: {
          statuses: [
            FundClosingStatusEnumType.Active,
            FundClosingStatusEnumType.Committed,
            FundClosingStatusEnumType.Transferred,
            FundClosingStatusEnumType.Executed,
            FundClosingStatusEnumType.Created,
            FundClosingStatusEnumType.Exited,
          ],
        },
        limit: 20,
      },
      notifyOnNetworkStatusChange: true,
    }
  );

  const [search, setSearch] = useState('');

  if (loading && !data) return <LoadingIndicator />;

  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  let closings = data.dashboard.fundClosings.nodes;
  closings = closings.map(closing => ({
    ...closing,
    unrealisedValue: closing.fund.statistics.multipleOnInvestment
      ? closing.fund.statistics.multipleOnInvestment * closing.amount
      : '-',
  }));
  const pageInfo = data.dashboard.fundClosings.pageInfo;

  if (!closings.length && !search) return null;

  const isV2Closing = closings.some(closing => closing.isV2Closing);

  return (
    <div>
      <p className="mt-4 text-md font-semibold">Fund Investments</p>
      <div className="mb-2">
        <TextField
          placeholder="Search for a fund"
          onChange={debounce(e => {
            setSearch(e.target.value);
            refetch({
              filters: {
                ...variables?.filters,
                text: e.target.value,
              },
            });
          }, 500)}
        />
      </div>
      {isV2Closing ? (
        <DataTable
          data={closings}
          emptyListTitle=""
          emptyListDescription=" "
          onClick={fundClosing => {
            navigate(`/invest/fund/${fundClosing.fund.id}`);
          }}
          columns={[
            {
              label: '',
              fieldName: 'fund.image',
              type: 'IMAGE',
            },
            {
              label: 'Fund',
              fieldName: 'fund.name',
            },
            // {
            //   label: 'Status',
            //   fieldName: 'status',
            //   type: 'STATUS',
            // },
            // {
            //   label: 'Investment Entity',
            //   fieldName: 'investmentEntity.name',
            // },

            {
              label: 'Committed Amount',
              fieldName: 'amount',
              type: 'CURRENCY',
            },
            {
              label: 'Drawdown Amount',
              fieldName: 'transferredAmount',
              type: 'CURRENCY',
            },
            {
              label: 'Total Investment',
              fieldName: 'investedAmount', // TODO: Change to corresponding field
              type: 'CURRENCY',
            },
            {
              label: 'Unrealised Value',
              fieldName: 'unrealisedValue',
              type: 'STRING', // TODO: change to CURRENCY
            },
            {
              label: 'Multiple',
              fieldName: 'fund.statistics.multipleOnInvestment',
              type: 'MULTIPLE',
            },
          ]}
          hasNextPage={pageInfo.hasNextPage}
          filterLoading={networkStatus === NetworkStatus.setVariables}
          paginationLoading={loading}
          onLoadMore={() =>
            fetchMore({
              variables: {
                cursor: pageInfo.cursor,
              },
            })
          }
        />
      ) : (
        <DataTable
          data={closings}
          emptyListTitle=""
          emptyListDescription=" "
          onClick={fundClosing => {
            navigate(`/invest/fund/${fundClosing.fund.id}`);
          }}
          columns={[
            {
              label: '',
              fieldName: 'fund.image',
              type: 'IMAGE',
            },
            {
              label: 'Fund',
              fieldName: 'fund.name',
            },
            {
              label: 'Status',
              fieldName: 'status',
              type: 'STATUS',
            },
            {
              label: 'Investment Entity',
              fieldName: 'investmentEntity.name',
            },
            {
              label: 'Invested',
              fieldName: 'amount',
              type: 'CURRENCY',
            },
            {
              label: 'Invest Date',
              fieldName: 'wiredAt',
              type: 'DATE',
            },
            {
              label: 'Unrealised Value',
              fieldName: 'unrealisedValue',
              type: 'CURRENCY',
            },
            {
              label: 'Multiple',
              fieldName: 'fund.statistics.multipleOnInvestment',
              type: 'MULTIPLE',
            },
          ]}
          hasNextPage={pageInfo.hasNextPage}
          filterLoading={networkStatus === NetworkStatus.setVariables}
          paginationLoading={loading}
          onLoadMore={() =>
            fetchMore({
              variables: {
                cursor: pageInfo.cursor,
              },
            })
          }
        />
      )}
    </div>
  );
};

export default FundCommitmentsOfUser;
